/**
 * NinjaGuide - Interactive AI Companion Panel
 * 
 * Features:
 * - Build Summary (7/7 files, engine, flow, archetype)
 * - AI Suggestions based on build result
 * - Quick-add expansion buttons
 * - Marketing ideas (slogan, campaign, monetization)
 * - Collapsible panel with localStorage persistence
 * 
 * States: collapsed, idle, building, complete, error
 */

import { useState, useEffect, useCallback } from 'react';
import {
    Bot, ChevronRight, ChevronLeft, Copy, Check,
    Lightbulb, Rocket, Megaphone, FileText, Loader2,
    AlertCircle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../utils/api';
import './NinjaGuide.css';

// ══════════════════════════════════════════════════════════════════
//  BUILD SUMMARY COMPONENT
// ══════════════════════════════════════════════════════════════════
const BuildSummary = ({ result }) => {
    const [copied, setCopied] = useState(false);

    const copyToClipboard = () => {
        const summary = `
Files: ${result.files?.length || 0}/7
Engine: ${result.engine || 'N/A'}
Flow: ${result.flowPolicy || 'N/A'}
Archetype: ${result.archetype || 'N/A'}
Seed: ${result.seed || 'N/A'}
Time: ${result.buildTime || 0}s
        `.trim();
        navigator.clipboard.writeText(summary);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Summary copied to clipboard');
    };

    const fileCount = result.files?.length || 0;
    const isComplete = fileCount === 7;

    return (
        <section className="ninja-guide__section">
            <h3 className="ninja-guide__section-title">
                <FileText className="h-4 w-4" />
                <span>Build Summary</span>
                <button
                    onClick={copyToClipboard}
                    className="ninja-guide__copy-btn"
                    title="Copy summary"
                >
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                </button>
            </h3>
            <ul className="ninja-guide__summary-list">
                <li>
                    <span>Files:</span>
                    <span className={isComplete ? 'text-emerald-400' : 'text-amber-400'}>
                        {fileCount}/7 {isComplete ? '✓' : '⚠'}
                    </span>
                </li>
                <li>
                    <span>Engine:</span>
                    <span className="font-mono text-xs">{result.engine || '—'}</span>
                </li>
                <li>
                    <span>Flow:</span>
                    <span>{result.flowPolicy || '—'}</span>
                </li>
                <li>
                    <span>Archetype:</span>
                    <span>{result.archetype || '—'}</span>
                </li>
                {result.seed && (
                    <li>
                        <span>Seed:</span>
                        <code className="text-xs bg-slate-800 px-1.5 py-0.5 rounded">
                            {result.seed.substring(0, 8)}...
                        </code>
                    </li>
                )}
                {result.buildTime && (
                    <li>
                        <span>Time:</span>
                        <span>{result.buildTime.toFixed(1)}s</span>
                    </li>
                )}
            </ul>
        </section>
    );
};

// ══════════════════════════════════════════════════════════════════
//  SUGGESTIONS COMPONENT
// ══════════════════════════════════════════════════════════════════
const Suggestions = ({ suggestions, onClick }) => {
    if (!suggestions?.length) return null;

    return (
        <section className="ninja-guide__section">
            <h3 className="ninja-guide__section-title">
                <Lightbulb className="h-4 w-4 text-amber-400" />
                <span>Suggestions</span>
            </h3>
            <ul className="ninja-guide__suggestions">
                {suggestions.map((suggestion, i) => (
                    <li
                        key={i}
                        className="ninja-guide__suggestion"
                        onClick={() => onClick?.(suggestion)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && onClick?.(suggestion)}
                    >
                        <span className="text-emerald-400">•</span>
                        {suggestion}
                    </li>
                ))}
            </ul>
        </section>
    );
};

// ══════════════════════════════════════════════════════════════════
//  EXPANSIONS COMPONENT
// ══════════════════════════════════════════════════════════════════
const Expansions = ({ expansions, onClick }) => {
    const defaultExpansions = [
        { id: 'blog', label: 'Blog Section', prompt: 'add blog section' },
        { id: 'faq', label: 'FAQ Accordion', prompt: 'add FAQ accordion' },
        { id: 'pricing', label: 'Pricing Grid', prompt: 'add pricing grid' },
        { id: 'contact', label: 'Contact Form', prompt: 'add contact form' },
        { id: 'newsletter', label: 'Newsletter', prompt: 'add newsletter signup' },
        { id: 'testimonials', label: 'Testimonials', prompt: 'add testimonials' },
    ];

    const items = expansions?.length ? expansions : defaultExpansions;

    return (
        <section className="ninja-guide__section">
            <h3 className="ninja-guide__section-title">
                <Rocket className="h-4 w-4 text-purple-400" />
                <span>Quick Add</span>
            </h3>
            <div className="ninja-guide__expansions">
                {items.map((exp) => (
                    <button
                        key={exp.id}
                        className="ninja-guide__expansion-btn"
                        onClick={() => onClick?.(exp.prompt)}
                        title={exp.description || exp.label}
                    >
                        + {exp.label}
                    </button>
                ))}
            </div>
        </section>
    );
};

// ══════════════════════════════════════════════════════════════════
//  MARKETING IDEAS COMPONENT
// ══════════════════════════════════════════════════════════════════
const MarketingIdeas = ({ ideas }) => {
    if (!ideas) return null;

    return (
        <section className="ninja-guide__section">
            <h3 className="ninja-guide__section-title">
                <Megaphone className="h-4 w-4 text-orange-400" />
                <span>Marketing Ideas</span>
            </h3>
            <div className="ninja-guide__marketing">
                {ideas.slogan && (
                    <p className="ninja-guide__marketing-item">
                        <strong>Slogan:</strong>
                        <span className="italic">"{ideas.slogan}"</span>
                    </p>
                )}
                {ideas.campaign && (
                    <p className="ninja-guide__marketing-item">
                        <strong>Campaign:</strong>
                        <span>{ideas.campaign}</span>
                    </p>
                )}
                {ideas.monetization && (
                    <p className="ninja-guide__marketing-item">
                        <strong>Monetization:</strong>
                        <span>{ideas.monetization}</span>
                    </p>
                )}
            </div>
        </section>
    );
};

// ══════════════════════════════════════════════════════════════════
//  MAIN NINJA GUIDE COMPONENT
// ══════════════════════════════════════════════════════════════════
const NinjaGuide = ({
    buildResult = null,
    isBuilding = false,
    error = null,
    onExpansionClick = null,
    onSuggestionClick = null,
    className = '',
}) => {
    const [userCollapsed, setUserCollapsed] = useState(() => {
        return localStorage.getItem('ninjaGuideCollapsed') === 'true';
    });
    const [suggestions, setSuggestions] = useState(null);

    // While a build is running, the guide is FORCED collapsed so it can
    // never hijack the viewport or appear as a separate “page”.
    // User’s manual collapsed preference still applies outside building.
    const isCollapsed = isBuilding ? true : userCollapsed;
    const setIsCollapsed = setUserCollapsed;

    // Persist collapsed state (user choice only — not the forced-during-build state)
    useEffect(() => {
        localStorage.setItem('ninjaGuideCollapsed', String(userCollapsed));
    }, [userCollapsed]);

    // Fetch suggestions when build completes
    useEffect(() => {
        if (buildResult && !isBuilding) {
            fetchSuggestions(buildResult);
        }
    }, [buildResult, isBuilding]);

    const fetchSuggestions = useCallback(async (result) => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/build/suggestions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    originalPrompt: result.originalPrompt || '',
                    engine: result.engine || '',
                    archetype: result.archetype || '',
                    flowPolicy: result.flowPolicy || '',
                    sections: result.sections || [],
                }),
            });
            if (response.ok) {
                const data = await response.json();
                setSuggestions(data);
            }
        } catch (err) {
            console.warn('Failed to fetch suggestions:', err);
        }
    }, []);

    // ── Collapsed state ──
    if (isCollapsed) {
        return (
            <div
                className={`ninja-guide ninja-guide--collapsed ${className}`}
                onClick={() => setIsCollapsed(false)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setIsCollapsed(false)}
            >
                <Bot className="h-5 w-5 text-emerald-400" />
                <span className="ninja-guide__collapsed-label">Guide</span>
                <ChevronLeft className="h-4 w-4 opacity-50" />
            </div>
        );
    }

    // ── Expanded state ──
    return (
        <aside className={`ninja-guide ${className}`}>
            {/* Header */}
            <header className="ninja-guide__header">
                <Bot className="h-5 w-5 text-emerald-400" />
                <h2 className="ninja-guide__title">Ninja Guide</h2>
                <button
                    className="ninja-guide__collapse-btn"
                    onClick={() => setIsCollapsed(true)}
                    aria-label="Collapse guide"
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            </header>

            {/* Content */}
            <div className="ninja-guide__content">
                {/* Error State */}
                {error && (
                    <div className="ninja-guide__error">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <p>{error}</p>
                    </div>
                )}

                {/* Building State */}
                {isBuilding && (
                    <div className="ninja-guide__building">
                        <Loader2 className="h-8 w-8 text-emerald-400 animate-spin" />
                        <p>Building your site...</p>
                        <span className="text-xs text-slate-500">Analyzing structure and content</span>
                    </div>
                )}

                {/* Complete State */}
                {!isBuilding && buildResult && (
                    <>
                        <BuildSummary result={buildResult} />
                        <Suggestions
                            suggestions={suggestions?.suggestions}
                            onClick={onSuggestionClick}
                        />
                        <Expansions
                            expansions={suggestions?.expansions}
                            onClick={onExpansionClick}
                        />
                        <MarketingIdeas ideas={suggestions?.marketingIdeas} />
                    </>
                )}

                {/* Idle State */}
                {!isBuilding && !buildResult && !error && (
                    <div className="ninja-guide__idle">
                        <Sparkles className="h-8 w-8 text-emerald-400/50 mb-3" />
                        <p>Start building to see guidance here.</p>
                        <p className="ninja-guide__hint">
                            Try: "A modern SaaS landing page with pricing"
                        </p>
                    </div>
                )}
            </div>
        </aside>
    );
};

export default NinjaGuide;
